// Einstellungen on the web: language and appearance (this browser), invoice settings and the git-remote opt-in (the
// workspace, synced to every device), the account, and the server-side GDPR export and deletion.
import { useEffect, useState } from 'react';
import { switchGuest } from '../api/guest.ts';
import { ProLock } from '../components/ProLock.tsx';
import type { Role, TeamResponse } from '../api/types.ts';
import { makeFormats } from '../lib/format.ts';
import { preferredLang, translator } from '../lib/i18n.ts';
import { applyTheme, langChoice, savePref, themeChoice, type LangChoice, type ThemeChoice } from '../lib/prefs.ts';
import { navigate } from '../lib/router.ts';
import type { TokenCostSettings } from '../../../packages/core/src/index.ts';
import { isOwner, tokenSettings, tokenSettingValue } from '../model/data.ts';
import * as m from '../model/mutations.ts';
import { Banner, Confirm, download, Field, ScreenHeader, SectionHeader, Select, TextArea, TextInput, Toggle } from '../components/common.tsx';
import { useFmt, useStore } from '../store/hooks.tsx';
import { StripeSettings } from './Invoices.tsx';
import { Button, SegmentedControl } from '../ui/ds.tsx';
import { HelpLinks } from './Help.tsx';
import { useAccountAuth } from '../app/auth.tsx';
import { Workspaces } from '../components/Workspaces.tsx';

type Tab = 'general' | 'invoices' | 'team' | 'account' | 'privacy';

export function Settings({ tab }: { tab: string }) {
  const store = useStore();
  const t = store.t;
  const current: Tab = tab === 'invoices' || tab === 'team' || tab === 'account' || tab === 'privacy' ? tab : 'general';
  return (
    <div className="screen settings">
      <ScreenHeader title={t('Einstellungen')}>
        <SegmentedControl<Tab> label={t('Bereich')} value={current} onChange={(v) => navigate({ name: 'settings', tab: v })} options={[
          { value: 'general', label: t('Allgemein') }, { value: 'invoices', label: t('Rechnungen') }, { value: 'team', label: t('Team') },
          { value: 'account', label: t('Konto') }, { value: 'privacy', label: t('Datenschutz') },
        ]} />
      </ScreenHeader>
      {current === 'general' ? <General /> : current === 'invoices' ? <Invoices />
        : current === 'team' ? (store.api.guest ? <NeedsAccount message={t('Teams gibt es nur mit Konto: Du lädst Personen per E-Mail ein, und alle erfassen im selben Arbeitsbereich.')} /> : <Team />)
        : current === 'account' ? (store.api.guest ? <GuestAccount /> : <Account />)
        : store.api.guest ? <GuestPrivacy /> : <Privacy />}
      <section className="tl-card settings-card"><h3 className="card-title">{t('Datenschutz und Support')}</h3><HelpLinks /></section>
    </div>
  );
}

function General() {
  const store = useStore();
  const t = store.t;
  const [lang, setLang] = useState<LangChoice>(langChoice());
  const [theme, setTheme] = useState<ThemeChoice>(themeChoice());
  const changeLang = (choice: LangChoice) => {
    setLang(choice);
    savePref('tally.lang', choice === 'system' ? null : choice);
    const next = choice === 'system' ? preferredLang() : choice;
    const tr = translator(next);
    store.setLanguage(next, tr, makeFormats(next, store.timeZone, tr));
  };
  return (
    <section className="tl-card settings-card">
      <Field label={t('Sprache')} hint={t('Gilt für diesen Browser.')}>
        <Select value={lang} onChange={(e) => changeLang(e.target.value as LangChoice)}>
          <option value="system">{t('Wie im Browser')}</option>
          <option value="de">Deutsch</option>
          <option value="en">English</option>
        </Select>
      </Field>
      <Field label={t('Darstellung')} hint={t('Hell und Dunkel sind gleichwertig; ohne Auswahl folgt Tellary dem System.')}>
        <SegmentedControl<ThemeChoice> label={t('Darstellung')} value={theme} onChange={(v) => { setTheme(v); savePref('tally.theme', v === 'system' ? null : v); applyTheme(v); }}
          options={[{ value: 'system', label: t('System') }, { value: 'light', label: t('Hell') }, { value: 'dark', label: t('Dunkel') }]} />
      </Field>
      <Field label={t('Zeitzone')} hint={t('Tage und Wochen richten sich nach der Zeitzone dieses Browsers.')}>
        <span className="settings-value">{store.timeZone}</span>
      </Field>
    </section>
  );
}

/** Team membership is separate from signing in and selecting the workspace. */
function Team() {
  const store = useStore();
  const t = store.t;
  const fmt = store.fmt;
  const [team, setTeam] = useState<TeamResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('member');
  const [working, setWorking] = useState(false);
  const [teamName, setTeamName] = useState(store.me?.workspace?.name ?? '');
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [removing, setRemoving] = useState<TeamResponse['members'][number] | null>(null);
  const load = () => store.api.team().then(setTeam).catch((e: unknown) => setError(store.describe(e)));
  useEffect(() => { if (!store.me?.workspace?.personal) void load(); }, [store]);
  const run = async (action: () => Promise<void>) => {
    if (working) return;
    setError(null); setWorking(true);
    try {
      await action();
      await load();
      await store.refresh();
      await store.refreshWorkspaces();
    } catch (e) {
      setError(store.describe(e));
    } finally { setWorking(false); }
  };
  const owner = team?.role === 'owner';
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const copy = async (url: string) => {
    setShareUrl(url); setCopied(false);
    try { await navigator.clipboard.writeText(url); setCopied(true); }
    catch { /* The selectable URL below also works when clipboard permission is unavailable. */ }
  };
  if (store.me?.workspace?.personal) return <>
    <section className="tl-card settings-card"><p className="note">{t('Dein privater Arbeitsbereich gehört nur dir. Erstelle ein Team, um gemeinsam zu arbeiten.')}</p></section>
    <Workspaces />
  </>;
  return (
    <>
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <section className="tl-card settings-card">
        {owner ? <Field label={t('Teamname')}><TextInput value={teamName} maxLength={120} disabled={working} onChange={(e) => setTeamName(e.target.value)} /></Field>
          : <h3 className="card-title">{store.me?.workspace?.name}</h3>}
        <div className="settings-actions">
          {owner ? <Button disabled={working || !teamName.trim() || teamName.trim() === store.me?.workspace?.name} onClick={() => void run(() => store.api.renameWorkspace(teamName.trim()))}>{t('Umbenennen')}</Button> : null}
          <Button variant="plain" disabled={working} onClick={() => setLeaving(true)}>{t('Team verlassen …')}</Button>
        </div>
      </section>
      <section className="tl-card settings-card">
        <p className="note">{owner
          ? t('Mitglieder erfassen ihre eigene Zeit und sehen nur diese. Inhaber legen Projekte und Kunden an, sehen die Zeit des Teams und rechnen sie ab. Abgerechnet wird pro Person: Arbeiten zwei gleichzeitig am selben Projekt, zählen beide Stunden.')
          : t('Du erfasst deine eigene Zeit und siehst nur diese. Projekte, Kunden und das Team verwalten die Inhaber.')}</p>
      </section>
      <SectionHeader title={t('Mitglieder')} caption={team ? String(team.members.length) : undefined} />
      <section className="tl-card table">
        {!team ? <p className="note">{t('Lädt …')}</p> : team.members.map((member) => (
          <div key={member.id} className="table__row team-row">
            <div className="team-row__who">
              <strong>{member.name || member.email || t('Mitglied')}{member.you ? ` (${t('du')})` : ''}</strong>
              {member.email ? <span className="note">{member.email}{owner ? ` · ${t('%lld Geräte', member.devices)}` : ''}</span> : null}
            </div>
            {owner && !member.you ? (
              <>
                <Select value={member.role} aria-label={t('Rolle')} className="select--auto" disabled={working} onChange={(e) => void run(() => store.api.setRole(member.id, e.target.value as Role))}>
                  <option value="member">{t('Mitglied')}</option>
                  <option value="owner">{t('Inhaber')}</option>
                </Select>
                <Button size="sm" variant="plain" disabled={working} onClick={() => setRemoving(member)}>{t('Entfernen')}</Button>
              </>
            ) : <span className="note">{member.role === 'owner' ? t('Inhaber') : t('Mitglied')}</span>}
          </div>
        ))}
      </section>
      {owner ? (
        <>
          <SectionHeader title={t('Einladen')} />
          <section className="tl-card settings-card">
            <div className="form-row">
              <Field label={t('E-Mail')}>
                <TextInput type="email" value={email} placeholder={t('name@firma.de')} onChange={(e) => setEmail(e.target.value)} />
              </Field>
              <Field label={t('Rolle')}>
                <Select value={role} onChange={(e) => setRole(e.target.value as Role)}>
                  <option value="member">{t('Mitglied')}</option>
                  <option value="owner">{t('Inhaber')}</option>
                </Select>
              </Field>
            </div>
            <p className="note">{t('Teile den Einladungslink mit der Person. Sie meldet sich mit dieser E-Mail-Adresse an und nimmt die Einladung an. Der Link gilt 14 Tage.')}</p>
            <div className="settings-actions">
              <Button variant="primary" icon="plus" disabled={!valid || working} onClick={() => void run(async () => { const result = await store.api.invite(email.trim(), role, store.lang); setShareUrl(result.shareUrl); setCopied(false); setEmail(''); })}>{t('Einladen')}</Button>
            </div>
          </section>
          {shareUrl ? <section className="tl-card settings-card">
            <Field label={t('Einladungslink')}><TextInput readOnly value={shareUrl} onFocus={(e) => e.target.select()} /></Field>
            <div className="settings-actions"><Button onClick={() => void copy(shareUrl)}>{copied ? t('Kopiert') : t('Link kopieren')}</Button></div>
          </section> : null}
          {team && team.invites.length > 0 ? (
            <>
              <SectionHeader title={t('Offene Einladungen')} />
              <section className="tl-card table">
                {team.invites.map((invite) => (
                  <div key={invite.id} className="table__row team-row">
                    <div className="team-row__who">
                      <strong>{invite.email}</strong>
                      <span className="note">{(invite.role === 'owner' ? t('Inhaber') : t('Mitglied')) + ' · ' + t('gilt bis %@', fmt.dayMonthYear(Date.parse(invite.expiresAt)))}</span>
                    </div>
                    {invite.shareUrl ? <Button size="sm" disabled={working} onClick={() => void copy(invite.shareUrl!)}>{t('Link kopieren')}</Button> : null}
                    <Button size="sm" variant="plain" disabled={working} onClick={() => void run(() => store.api.revokeInvite(invite.id))}>{t('Zurückziehen')}</Button>
                  </div>
                ))}
              </section>
            </>
          ) : null}
        </>
      ) : null}
      {leaving ? <Confirm title={t('Team verlassen?')} danger confirm={t('Verlassen')} cancel={t('Abbrechen')} onClose={() => setLeaving(false)}
        message={t('Du verlierst den Team-Zugang. Deine erfasste Zeit bleibt dort; deine privaten Daten bleiben erhalten. Der letzte Inhaber muss zuerst eine andere Person zum Inhaber machen.')}
        onConfirm={() => { setLeaving(false); void run(() => store.leaveWorkspace()); }} /> : null}
      {removing ? (
        <Confirm title={t('%@ entfernen?', removing.name || removing.email || t('Mitglied'))} danger confirm={t('Entfernen')} cancel={t('Abbrechen')}
          onClose={() => setRemoving(null)}
          message={t('Die Geräte der Person melden sich ab und sie verliert den Zugang. Ihre erfasste Zeit bleibt im Arbeitsbereich.')}
          onConfirm={() => { const id = removing.id; setRemoving(null); void run(() => store.api.removeMember(id)); }} />
      ) : null}
    </>
  );
}

function Invoices() {
  const store = useStore();
  const t = store.t;
  const settings = store.ws.settings;
  const [sender, setSender] = useState(settings.get('invoiceSender') ?? '');
  const set = (key: 'vatPercent' | 'invoiceSender' | 'defaultRoundingMinutes', value: string | null) => void store.apply(m.setSetting(key, value, store.clock()));
  const vat = settings.get('vatPercent') ?? '19';
  if (!isOwner(store.ws)) {
    return <section className="tl-card settings-card"><p className="note">{t('Rechnungseinstellungen ändern die Inhaber des Arbeitsbereichs.')}</p></section>;
  }
  // Invoices are Pro (spec/pro.md); the server refuses these settings without it.
  if (!store.pro) {
    return <section className="tl-card settings-card"><ProLock text={t('Absender, Umsatzsteuer, Token-Kosten und Stripe-Rechnungen gibt es mit Pro.')} /></section>;
  }
  return (
    <>
    <section className="tl-card settings-card">
      <p className="note">{t('Gilt für den ganzen Arbeitsbereich und wird mit deinen Macs abgeglichen.')}</p>
      <Field label={t('Umsatzsteuer')}>
        <Select value={['19', '7', 'exempt'].includes(vat) ? vat : '19'} onChange={(e) => set('vatPercent', e.target.value)}>
          <option value="19">19 %</option>
          <option value="7">7 %</option>
          <option value="exempt">{t('Keine (Kleinunternehmer, § 19 UStG)')}</option>
        </Select>
      </Field>
      <Field label={t('Absender auf der Rechnungszusammenfassung')} hint={t('Name, Firma und Anschrift, je eine Zeile.')}>
        <TextArea rows={4} value={sender} onChange={(e) => setSender(e.target.value)} onBlur={() => { if (sender !== (settings.get('invoiceSender') ?? '')) set('invoiceSender', sender.trim() ? sender : null); }} />
      </Field>
      <Field label={t('Rundung für neue Projekte')} hint={t('Gerundet wird nur im Export, pro zusammengeführtem Intervall und immer aufwärts. Die App zeigt exakte Zeiten.')}>
        <Select value={settings.get('defaultRoundingMinutes') ?? '0'} onChange={(e) => set('defaultRoundingMinutes', e.target.value)}>
          <option value="0">{t('Keine')}</option>
          <option value="1">{t('1 min')}</option>
          <option value="6">{t('6 min')}</option>
          <option value="15">{t('15 min')}</option>
        </Select>
      </Field>
      <TokenCostFields />
    </section>
    <StripeSettings />
    </>
  );
}

/** Token costs as their own invoice line (spec §9c): the dollar rate and a markup. */
function TokenCostFields() {
  const store = useStore();
  const t = store.t;
  const fmt = useFmt();
  const current = tokenSettings(store.ws);
  const shown = (s: TokenCostSettings | null) => ({
    rate: s ? fmt.number(s.usdRate / 10_000, s.usdRate % 100 === 0 ? 2 : 4) : '',
    markup: s ? fmt.number(s.markup / 100, s.markup % 100 === 0 ? 0 : 2) : '',
  });
  const [draft, setDraft] = useState(shown(current));
  const write = (s: TokenCostSettings | null) => {
    void store.apply(m.setSetting('tokenCosts', s ? tokenSettingValue(s) : null, store.clock()));
    setDraft(shown(s));
  };
  // "0,92" (German, dots group thousands) or "0.92".
  const parse = (text: string) => { const v = text.trim(); return Number(v.includes(',') ? v.replace(/\./g, '').replace(',', '.') : v); };
  const commit = () => {
    if (!current) return;
    const rate = parse(draft.rate);
    const markup = draft.markup.trim() === '' ? 0 : parse(draft.markup);
    const next = {
      usdRate: Number.isFinite(rate) && rate > 0 && rate <= 100 ? Math.round(rate * 10_000) : current.usdRate,
      markup: Number.isFinite(markup) && markup >= 0 && markup <= 1000 ? Math.round(markup * 100) : current.markup,
    };
    if (next.usdRate !== current.usdRate || next.markup !== current.markup) write(next);
    else setDraft(shown(current));
  };
  return (
    <>
      <Field inline label={t('Token-Kosten als eigene Position')}>
        <Toggle checked={current !== null} label={t('Token-Kosten als eigene Position')} onChange={(on) => write(on ? { usdRate: 9000, markup: 0 } : null)} />
      </Field>
      {current ? (
        <div className="settings-pair">
          <Field label={t('1 US-Dollar in Euro')}>
            <TextInput inputMode="decimal" value={draft.rate} placeholder="0,90" onChange={(e) => setDraft({ ...draft, rate: e.target.value })} onBlur={commit} />
          </Field>
          <Field label={t('Aufschlag in %')}>
            <TextInput inputMode="decimal" value={draft.markup} placeholder="0" onChange={(e) => setDraft({ ...draft, markup: e.target.value })} onBlur={commit} />
          </Field>
        </div>
      ) : null}
      <p className="note">{t('Nur Kosten, die ein Anbieter meldet (Claude Code über OpenTelemetry); aus Token-Zahlen schätzt Tellary nichts. Die Kosten stehen getrennt von der Zeit auf der Rechnung, jede Session in dem Zeitraum, in dem sie begann. Trag den aktuellen Kurs ein.')}</p>
    </>
  );
}

function Account() {
  const store = useStore();
  const auth = useAccountAuth();
  const t = store.t;
  const syncRemote = store.ws.settings.get('syncGitRemote') === '1';
  return (
    <>
      {auth.error ? <Banner tone="danger">{auth.error}</Banner> : null}
      <section className="tl-card settings-card">
        <Field label={t('Angemeldet als')}>
          <span className="settings-value">{store.me?.user.email ?? store.memberName}</span>
        </Field>
        <div className="settings-actions">
          <Button onClick={() => navigate({ name: 'integrations' })} icon="laptop">{t('Geräte verwalten')}</Button>
          <Button variant="plain" icon="logout" disabled={auth.pending} onClick={() => void auth.signOut()}>{t('Abmelden')}</Button>
        </div>
      </section>
      <Workspaces />
      <SectionHeader title={t('Was abgeglichen wird')} />
      <section className="tl-card settings-card">
        <p className="note">{t('Einträge mit Verlauf, Projekte, Kunden, Remote- und Branch-Regeln, Sessions mit Spans, Korrekturen, Token-Zahlen und Rechnungseinstellungen. Nie übertragen werden Prompts, Code, Tool-Ausgaben, Arbeitsordner und Ordner-Regeln.')}</p>
        <Field inline label={t('Git-Remotes der Sessions abgleichen')} hint={t('Nötig für Remote-Regeln im Web und auf anderen Geräten. Die Adresse eines Repos kann den Kundennamen verraten.')}>
          <Toggle checked={syncRemote} disabled={!isOwner(store.ws)} label={t('Git-Remotes der Sessions abgleichen')} onChange={(on) => void store.apply(m.setSetting('syncGitRemote', on ? '1' : '0', store.clock()))} />
        </Field>
      </section>
    </>
  );
}

/** A guest in a place that needs an account: what it would bring, and the way there. */
function NeedsAccount({ message }: { message: string }) {
  const t = useStore().t;
  return (
    <section className="tl-card settings-card">
      <p className="note">{message}</p>
      <div className="settings-actions">
        <Button variant="primary" onClick={() => switchGuest(false)}>{t('Jetzt Konto erstellen')}</Button>
        <Button onClick={() => switchGuest(false)}>{t('Anmelden')}</Button>
      </div>
    </section>
  );
}

function GuestAccount() {
  const store = useStore();
  const t = store.t;
  return (
    <>
      <section className="tl-card settings-card">
        <Field label={t('Du nutzt Tellary als Gast')}>
          <span className="settings-value">{t('Nur in diesem Browser')}</span>
        </Field>
        <p className="note">{t('Mit einem Konto gleicht Tellary Einträge, Projekte und Agent-Zeiten zwischen deinen Geräten ab. Prompts, Code und Arbeitsordner gehören nicht zum Gerätesync. Angaben zum Betreiber und Speicherort findest du in der Datenschutzerklärung deines Servers.')}</p>
        <p className="note">{t('Deine Daten aus dem Gastmodus kommen nach der Anmeldung in dein Konto.')}</p>
        <div className="settings-actions">
          <Button variant="primary" icon="user" onClick={() => switchGuest(false)}>{t('Jetzt Konto erstellen')}</Button>
          <Button onClick={() => switchGuest(false)}>{t('Anmelden')}</Button>
        </div>
      </section>
    </>
  );
}

/** A guest's data lives in this browser: export it, or delete it and start over. */
function GuestPrivacy() {
  const store = useStore();
  const t = store.t;
  const [confirm, setConfirm] = useState(false);
  const exportData = async () => download('tellary-export.json', await store.api.exportAccount(), 'application/json');
  return (
    <>
      <section className="tl-card settings-card">
        <h3 className="card-title">{t('Deine Daten in diesem Browser')}</h3>
        <p className="note">{t('Als Gast speichert Tellary deine Projekte und Einträge nur in diesem Browser. Löschst du die Website-Daten des Browsers, sind sie weg; mit einem Konto liegen sie zusätzlich auf dem Server.')}</p>
        <div className="settings-actions">
          <Button icon="download" onClick={() => void exportData()}>{t('Alle Daten exportieren (JSON)')}</Button>
        </div>
      </section>
      <section className="tl-card settings-card">
        <h3 className="card-title danger-text">{t('Gastdaten löschen')}</h3>
        <p className="note">{t('Löscht alle Projekte, Kunden und Einträge des Gastmodus aus diesem Browser.')}</p>
        <div className="settings-actions">
          <Button variant="danger" onClick={() => setConfirm(true)}>{t('Gastdaten löschen …')}</Button>
        </div>
      </section>
      {confirm ? (
        <Confirm title={t('Gastdaten wirklich löschen?')} danger confirm={t('Endgültig löschen')} cancel={t('Abbrechen')} onClose={() => setConfirm(false)}
          message={t('Das lässt sich nicht rückgängig machen. Exportiere deine Daten vorher, wenn du sie behalten willst.')}
          onConfirm={() => { void store.api.deleteAccount().then(() => switchGuest(false)); }} />
      ) : null}
    </>
  );
}

function Privacy() {
  const store = useStore();
  const auth = useAccountAuth();
  const t = store.t;
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const exportData = async () => {
    try {
      download('tellary-export.json', await store.api.exportAccount(), 'application/json');
    } catch (e) {
      setError(store.describe(e));
    }
  };
  return (
    <>
      {error || auth.error ? <Banner tone="danger">{error ?? auth.error}</Banner> : null}
      <section className="tl-card settings-card">
        <h3 className="card-title">{t('Deine Daten auf dem Server')}</h3>
        <p className="note">{t('Der gewählte Server speichert dein Konto, deine Geräte und die abgeglichenen Daten. Roh-Events enthalten Zeitpunkte, Event-Arten und erlaubte Metadaten; keine Prompts, Quelltexte oder Tool-Ausgaben.')}</p>
        <div className="settings-actions">
          <Button icon="download" onClick={() => void exportData()}>{t('Alle Daten exportieren (JSON)')}</Button>
        </div>
      </section>
      <section className="tl-card settings-card">
        <h3 className="card-title danger-text">{t('Konto löschen')}</h3>
        <p className="note">{t('Löscht dein Konto und einen nur von dir genutzten Arbeitsbereich vom Server. In Teams werden deine Konto-Zuordnung und dein Mitgliedsname entfernt. Die gemeinsame Zeithistorie und eingetragene Inhalte bleiben erhalten; darin können weiterhin personenbezogene Angaben stehen. Der letzte Inhaber muss vorher die Inhaberschaft übertragen. Lokale Kopien, Exporte und Sicherungen auf deinen Geräten bleiben erhalten.')}</p>
        <div className="settings-actions">
          <Button variant="danger" onClick={() => setConfirm(true)}>{t('Konto löschen …')}</Button>
        </div>
      </section>
      {confirm ? (
        <Confirm title={t('Konto wirklich löschen?')} danger confirm={t('Endgültig löschen')} cancel={t('Abbrechen')} onClose={() => setConfirm(false)}
          message={t('Das lässt sich nicht rückgängig machen. Exportiere deine Daten vorher, wenn du sie behalten willst.')}
          onConfirm={() => { void store.api.deleteAccount().then(() => auth.signOut()).catch((e: unknown) => setError(store.describe(e))); }} />
      ) : null}
    </>
  );
}
